/** @jsxImportSource @emotion/react */
import 'twin.macro';

import { TranscriptSkeleton } from 'components/shared/Skeleton/TranscriptSkeleton';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { seamSkeletonRowCount } from 'utils/transcriptChunk.util';

import { resolveTranscriptScrollRootEl } from './useTranscriptChunkLoadingCue';

const TRANSCRIPT_VIEWPORT_OVERLAY_Z_INDEX = 10000;

type ViewportRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

/** Content-box rect of the scroll root (excludes scrollbar gutters / borders). */
export const readScrollRootRect = (): ViewportRect | null => {
  const root = resolveTranscriptScrollRootEl();
  if (!root) return null;
  const rect = root.getBoundingClientRect();
  const width = Math.round(root.clientWidth || 0);
  const height = Math.round(root.clientHeight || 0);
  // client* can be 1×N while layout is still settling — fall back to border-box.
  if (width < 2 || height < 2) {
    const scrollbarWidth = Math.max(0, root.offsetWidth - root.clientWidth);
    const scrollbarHeight = Math.max(0, root.offsetHeight - root.clientHeight);
    return {
      top: Math.max(0, rect.top),
      left: Math.max(0, rect.left),
      width: Math.max(1, Math.round(rect.width - scrollbarWidth)),
      height: Math.max(1, Math.round(rect.height - scrollbarHeight)),
    };
  }
  return {
    top: Math.max(0, rect.top + root.clientTop),
    left: Math.max(0, rect.left + root.clientLeft),
    width,
    height,
  };
};

type Props = {
  active: boolean;
  waitCursor?: boolean;
};

/** Full-viewport transcript skeleton overlay — blocks input while chunk loads. */
export const TranscriptViewportLoadingOverlay = ({
  active,
  waitCursor = false,
}: Props) => {
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<ViewportRect | null>(null);
  const paintRect = active ? rect ?? readScrollRootRect() : null;

  useLayoutEffect(() => {
    if (!active) {
      setRect(null);
      return;
    }
    setRect(readScrollRootRect());
  }, [active]);

  useEffect(() => {
    if (!active) {
      return;
    }

    const root = resolveTranscriptScrollRootEl();
    if (!root) return;

    let raf1 = 0;
    let raf2 = 0;
    const syncRect = () => setRect(readScrollRootRect());

    // Activate + double rAF so layout/scrollbar metrics settle before paint.
    syncRect();
    raf1 = requestAnimationFrame(() => {
      syncRect();
      raf2 = requestAnimationFrame(syncRect);
    });

    window.addEventListener('resize', syncRect);
    window.addEventListener('scroll', syncRect, true);
    root.addEventListener('scroll', syncRect, { passive: true });

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => syncRect())
        : null;
    resizeObserver?.observe(root);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener('resize', syncRect);
      window.removeEventListener('scroll', syncRect, true);
      root.removeEventListener('scroll', syncRect);
      resizeObserver?.disconnect();
    };
  }, [active]);

  useEffect(() => {
    if (!active) return;

    const blockScrollIntent = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const overlay = overlayRef.current;
    overlay?.addEventListener('wheel', blockScrollIntent, {
      passive: false,
      capture: true,
    });
    overlay?.addEventListener('touchmove', blockScrollIntent, {
      passive: false,
      capture: true,
    });

    return () => {
      overlay?.removeEventListener('wheel', blockScrollIntent, true);
      overlay?.removeEventListener('touchmove', blockScrollIntent, true);
    };
  }, [active, rect]);

  if (!active) return null;

  const effectiveRect = paintRect ?? readScrollRootRect();
  if (!effectiveRect) return null;

  const skeletonRows = Math.max(
    4,
    seamSkeletonRowCount(effectiveRect.height, 24),
  );

  return createPortal(
    <div
      ref={overlayRef}
      data-transcript-viewport-overlay="1"
      aria-busy="true"
      aria-label="Loading transcript"
      style={{
        position: 'fixed',
        top: effectiveRect.top,
        left: effectiveRect.left,
        width: effectiveRect.width,
        height: effectiveRect.height,
        zIndex: TRANSCRIPT_VIEWPORT_OVERLAY_Z_INDEX,
        pointerEvents: 'auto',
        cursor: waitCursor ? 'wait' : 'default',
        touchAction: 'none',
        overscrollBehavior: 'none',
        overflow: 'hidden',
        boxSizing: 'border-box',
        background: '#fff',
      }}
    >
      <div
        style={{
          width: '100%',
          minHeight: '100%',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          background: '#fff',
        }}
      >
        <TranscriptSkeleton count={skeletonRows} />
      </div>
    </div>,
    document.body,
  );
};
